import { useState, useEffect } from 'react';
import { Link, useParams, useLocation } from "react-router-dom";
import { format, parse } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { useHeaderContext } from "../../context/headerContext";
import { useDrawerContext } from "../../context/drawerContext";
import { useUserContext } from '../../context/userContext';
import { useThemeContext } from '../../context/themeContext';
import { fetchUserProfile } from '../../utils/FetchUserProfile';
import { fetchAdditionalICEContacts } from '../../utils/FetchAdditionalICEContacts';
import { fetchMedicalHistory } from '../../utils/FetchMedicalHistory';
import { fetchAllergies } from '../../utils/FetchAllergies';
import { fetchMedications } from '../../utils/FetchMedications';
import { fetchHistoricalIncidentsForParticipant } from '../../utils/FetchHistoricalIncidentsForParticipant';
import { fetchBands } from '../../utils/FetchBands';
import VoiceNote from '../../components/VoiceNote';
import ParticipantCard from '../../components/ParticipantCard';
import BandCard from '../../components/BandCard';
import DropDown from '../../components/Dropdown';
import Button from '../../components/Button';
import TextInput from '../../components/TextInput';
import CloseIcon from "/public/images/icons/close.svg?react";
import ChevronRight from "/public/images/icons/chevronRight.svg?react";
import IncidentIcon from '/public/images/icons/incidentHistoryIcon.svg?react';
import Toggle from '../../components/Toggle';
import fetchWithRefresh from '../../utils/fetchWithRefresh';
import { useLiteDrawer } from '../../hooks/useLiteDrawer';
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";
import { LogErrors } from "../../utils/LogErrors";

export default function ParticipantProfile() {
    const { setTitle, setBackButton, setEditFunc } = useHeaderContext();
    const { setOpen, setDrawerContent } = useDrawerContext();
    const { user, logout, updateTokens, selectedVenue } = useUserContext();
    const { theme } = useThemeContext();
    const { showLiteDrawer } = useLiteDrawer();
    const navigate = useNavigate();

    //Get the participant
    const participantId = useParams().participantId;
    const { state } = useLocation();
    const [participantProfile, setParticipantProfile] = useState(null);

    const [medicalConditions, setMedicalConditions] = useState([]);
    const [allergies, setAllergies] = useState([]);
    const [medications, setMedications] = useState([]);
    const [incidentHistory, setIncidentHistory] = useState([]);
    const [otherEmergencyContacts, setOtherEmergencyContacts] = useState([]);
    const [checkedInBand, setCheckedInBand] = useState(null);
    const [email, setEmail] = useState('');
    const [emailError, setEmailError] = useState('');
    const [triggersMilestone, setTriggersMilestone] = useState(false);
    const [bands, setBands] = useState([]);
    const [transferStatus, setTransferStatus] = useState(false);  
    
    const [milestoneRequirements, setMilestoneRequirements] = useState(false);

    const fetchProfile = async () => {
      const data = await fetchUserProfile(participantId, user?.token);
      setParticipantProfile(data ?? state?.user ?? {});
    }; 

    const fetchUserAdditionalICEContacts = async () => {
      const data = await fetchAdditionalICEContacts(participantId, user?.token);
      setOtherEmergencyContacts(data ?? []);
    }

    const fetchUserMedicalHistory = async () => {
      const data = await fetchMedicalHistory(participantId, user?.token);
      setMedicalConditions(data ?? []);
    }

    const fetchUserAllergies = async () => {
      const data = await fetchAllergies(participantId, user?.token);
      setAllergies(data ?? []);
    }

    const fetchUserMedications = async () => {
      const data = await fetchMedications(participantId, user?.token);
      setMedications(data ?? []);
    }

    const fetchUserIncidents = async () => {
      const data = await fetchHistoricalIncidentsForParticipant(participantId, user?.token);
      setIncidentHistory(data ?? []);
    }

    const fetchUserBands = async () => {
      const data = await fetchBands(participantId, user?.token);
      setBands(data);
    }

    useEffect(() => {
      if(participantId && user?.token) {
        fetchProfile();
        fetchUserAdditionalICEContacts();
        fetchUserMedicalHistory();
        fetchUserAllergies();
        fetchUserMedications();
        fetchUserIncidents();
        fetchUserBands();
      }

      setBackButton(true);
  }, [participantId, user?.token]);

    useEffect(() => {
      if(checkedInBand?.participantId && checkedInBand?.ZoneID) {
        fetchMilestoneRequirements();
      }
  }, [participantId, checkedInBand]);
  
    useEffect(() => {
      if(participantProfile){
        if(theme === "staff"){
          setTitle(`${participantProfile?.ParticipantName}`);
        } else {
          setTitle(`${participantProfile?.ParticipantName}'s PROFILE`);
        }

        setTransferStatus(participantProfile?.TransferPending === 1 ? true : false);
      }

    }, [participantProfile]);


    useEffect(() => {
      if(bands?.length > 0){
        setCheckedInBand(bands.find(band => band.BandStateVerbose === "CHECKED IN"))
      }
    }, [bands])

    // If its a participant it must be their profile so allow editing
    useEffect(() => {
      if(participantProfile && participantId && medicalConditions && allergies && medications && otherEmergencyContacts && theme === "participant"){
        setEditFunc(() => {
          navigate(`/participant/${participantId}/edit/`, { 
            state: { 
              participant: participantProfile, 
              band: bands[0],
              medicalConditions: medicalConditions,
              allergies: allergies,
              medications: medications,
              otherEmergencyContacts: otherEmergencyContacts
            }
          })
        });
      }
    }, [participantProfile, participantId, medicalConditions, allergies, medications, otherEmergencyContacts, theme]);


    useEffect(() => {
      if(emailError){
        transferParticipant();
      }
    }, [emailError])

    const transferParticipantForm = async (event) => {
      event.preventDefault();
    
      const newEmail = new FormData(event.currentTarget);
      const emailValue = newEmail.get("email");
    
      await Sentry.startSpan({ name: "Transfer Participant", op: "http.request" }, async (span) => {
        try {
          const url = `${import.meta.env.VITE_API_URL}Profile/CanTransferParticipant?emailAddress=${emailValue}`;
          span.setAttribute("http.url", url);
          span.setAttribute("emailAddress", emailValue);
    
          // Check if the participant can accept the transfer
          const canTransferResponse = await fetchWithRefresh(url, {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${user?.token}`,
            },
          }, logout, updateTokens, (message) => { toast.info(message);});
    
          const canTransferText = await canTransferResponse.text();
    
          if (canTransferResponse.ok && canTransferText.includes("Can transfer")) {
            // Proceed with the transfer process
            const transferUrl = `${import.meta.env.VITE_API_URL}Profile/InitiateTransferParticipantProcess?emailAddress=${emailValue}&participantId=${participant?.ParticipantId}`;
            span.setAttribute("transfer_url", transferUrl);
    
            const req = await fetchWithRefresh(transferUrl, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${user?.token}`,
              },
            }, logout, updateTokens, (message) => { toast.info(message);});
    
            const contentType = req.headers.get("Content-Type");
            let res;
            if (contentType && contentType.includes("application/json")) {
              res = await req.json(); // Parse as JSON
            } else {
              res = await req.text(); // Parse as text
            }
    
            if (req.ok) {
              setEmailError(""); // Clear any previous error
              setTransferStatus(true);
              setDrawerContent(
                <div className="w-full">
                  <div className="flex items-center justify-between w-full">
                    <br />
                    <h2 className="leading-5 tracking-widest uppercase text-[12px]">
                      TRANSFER
                    </h2>
                    <CloseIcon className='cursor-pointer' onClick={() => setOpen(false)} />
                  </div>
                  <div className="mt-6 flex flex-col gap-4 justify-stretch">
                    <h3 className="text-[24px]">Transfer request sent</h3>
                    <p className="text-sportsDarkGrey">
                      Your request has been sent to <span className='font-bold'>{emailValue}</span>. The participant's band(s) have been temporarily deactivated until they confirm their details.
                    </p>
                    <Button variant="primary"
                      onClick={() => {
                        setOpen(false);
                        navigate('/')
                      }}
                    >
                      Close
                    </Button>
                  </div>
                </div>
              );
              span.setStatus("ok");
            } else {
              LogErrors(user, logout, updateTokens, "Error initiating transfer process");
              span.setStatus("error");
              throw new Error("Failed to initiate transfer process");
            }
          } else {
            setEmailError("Cannot transfer to this email address. " + canTransferText);
            span.setStatus("error");
          }
        } catch (error) {
          Sentry.captureException(error, { extra: { emailValue } });
          LogErrors(user, logout, updateTokens, "Error during transfer process:", error);
          setEmailError("An error occurred while validating the email address.");
          span.setStatus("error");
        } finally {
          span.end();
        }
      });
    };

    const cancelTransferRequest = async () => {
      await Sentry.startSpan({ name: "Cancel Transfer Request", op: "http.request" }, async (span) => {
        try {
          const url = `${import.meta.env.VITE_API_URL}Profile/CancelTransferParticipant?participantId=${participantId}`;
          span.setAttribute("http.url", url);
          span.setAttribute("participantId", participantId);
    
          const response = await fetchWithRefresh(url, {
            method: 'PUT',
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${user.token}`,
            },
          }, logout, updateTokens, (message) => { toast.info(message);});
    
          if (response.ok) {
            setTransferStatus(false);
            setDrawerContent(
              <div className="w-full">
                <div className="flex items-center justify-between w-full">
                  <br />
                  <h2 className="leading-5 tracking-widest uppercase text-[12px]">
                    TRANSFER
                  </h2>
                  <CloseIcon className='cursor-pointer' onClick={() => setOpen(false)} />
                </div>
                <div className="mt-6 flex flex-col gap-4 justify-stretch">
                  <h3>Transfer request cancelled</h3>
                  <p className="text-sportsDarkGrey">
                    Transfer request has been successfully cancelled.
                  </p>
                  <div className="flex gap-6">
                    <Button variant='primary' className="w-full" 
                      onClick={() => {
                        setOpen(false);
                        navigate('/')
                      }}>
                      Close
                    </Button>
                  </div>
                </div>
              </div>
            );
            span.setStatus("ok");
          } else {
            const contentType = response.headers.get("Content-Type");
            let responseData;
            if (contentType && contentType.includes("application/json")) {
              responseData = await response.json(); // Parse as JSON
            } else {
              responseData = await response.text(); // Parse as text
            }
            LogErrors(user, logout, updateTokens, 'Error Cancelling Transfer ' + responseData);
            span.setStatus("error");
            throw new Error("Failed to cancel transfer request");
          }
        } catch (error) {
          Sentry.captureException(error, { extra: { participantId } });
          LogErrors(user, logout, updateTokens, "Error: " + error);
          span.setStatus("error");
        } finally {
          span.end();
        }
      });
    };

    const transferParticipant = () => {
      setOpen(true);
      setDrawerContent(
        <div className="w-full">
          <div className="flex items-center justify-between w-full">
            <br />
            <h2 className="leading-5 tracking-widest uppercase text-[12px]">
              TRANSFER
            </h2>
            <CloseIcon className='cursor-pointer' onClick={() => setOpen(false)} />
          </div>
          <div className="mt-6 flex flex-col gap-4 justify-stretch">
            <h3 className="text-[24px]">Transfer participant</h3>
            <p className=" text-sportsDarkGrey">
              To initiate a participant transfer, enter the email address of the person you wish to transfer the profile to.
            </p>
  
            <form onSubmit={transferParticipantForm} className="flex flex-col gap-y-[24px]">
              <div>
                <TextInput
                  label="Email"
                  name="email"
                  defaultValue={email}
                  onChange={(e) => setEmail(e.target.value)}
                  type="email"
                />
                {emailError && <p className="text-red-500 mt-1">{emailError}</p>}
              </div>
              <Button variant="primary" submit>
                Confirm & Continue
              </Button>
            </form>
          </div>
        </div>
      );
    };
  
    const cancelTransfer = () => {
      setOpen(true);
      setDrawerContent(
        <div className="w-full">
          <div className="flex items-center justify-between w-full">
            <br />
            <h2 className="leading-5 tracking-widest uppercase text-[12px]">
              TRANSFER IN PROGRESS
            </h2>
            <CloseIcon className='cursor-pointer' onClick={() => setOpen(false)} />
          </div>
          <div className="mt-6 flex flex-col gap-4 justify-stretch">
            <h3 className="text-[24px]">Cancel Transfer</h3>
            <p className=" text-sportsDarkGrey">
              Cancel the current transfer and keep this participant profile in your account.
            </p>
            <div className="flex gap-6">
              <Button variant='primary' className="w-full" onClick={cancelTransferRequest}>Cancel Transfer</Button>
            </div>
          </div>
        </div>
      )
    }

    const unassignBand = async () => {
      await Sentry.startSpan({ name: "Unassign Band", op: "http.request" }, async (span) => {
        try {
          const guestBand = bands.find(b => b.IsGuestBand === 1);
          const url = `${import.meta.env.VITE_API_URL}GuestBands/DeActivateGuestBand?bandId=${guestBand.BandID}`;
          span.setAttribute("http.url", url);
          span.setAttribute("bandId", guestBand?.BandID);
    
          const response = await fetchWithRefresh(url, {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${user?.token}`,
            },
          }, logout, updateTokens, (message) => { toast.info(message);});
    
          if (response.ok) {
            navigate(`/guest-bands/`);
            setOpen(true);
            setDrawerContent(
              <div className="w-full">
                <div className="flex items-center justify-between w-full">
                  <br />
                  <h2 className="leading-5 tracking-widest uppercase text-[12px]">
                    UNASSIGN BAND
                  </h2>
                  <CloseIcon className='cursor-pointer' onClick={() => setOpen(false)} />
                </div>
                <div className="mt-6 flex flex-col gap-4 justify-stretch">
                  <h3 className="text-[24px]">Successfully unassigned {participantProfile?.FriendlyName} from {participantProfile?.ParticipantName}!</h3>
                  <Button variant='primary' onClick={() => setOpen(false)}>Close</Button>
                </div>
              </div>
            );
            span.setStatus("ok");
          } else {
            const error = await response.text();
            setOpen(true);
            setDrawerContent(
              <div className="w-full">
                <div className="flex items-center justify-between w-full">
                  <br />
                  <h2 className="leading-5 tracking-widest uppercase text-[12px]">
                    FAILED TO UNASSIGN BAND
                  </h2>
                  <CloseIcon className='cursor-pointer' onClick={() => setOpen(false)} />
                </div>
                <div className="mt-6 flex flex-col gap-4 justify-stretch">
                  <h3 className="text-[24px]">{error}</h3>
                  <Button variant='primary' onClick={() => setOpen(false)}>Close</Button>
                </div>
              </div>
            );
    
            LogErrors(user, logout, updateTokens, "Failed to check band out of zone. Status: " + response.status + ". " + response.statusText);
            span.setStatus("error");
            throw new Error(`Failed to unassign band: ${error}`);
          }
        } catch (error) {
          Sentry.captureException(error, { extra: { bandId: bands.find(b => b.IsGuestBand === 1)?.BandID } });
          LogErrors(user, logout, updateTokens, "Couldn't reach checkout endpoint. Error: " + error);
          span.setStatus("error");
        } finally {
          span.end();
        }
      });
    };
  
    const confirmUnassignBand = () => {
      setOpen(true);
      setDrawerContent(
          <div className="w-full">
              <div className="flex items-center justify-between w-full">
                  <br />
                  <h2 className="leading-5 tracking-widest uppercase text-[12px]">
                      UNASSIGN BAND
                  </h2>
                  <CloseIcon className='cursor-pointer' onClick={() => setOpen(false)} />
              </div>
              <div className="mt-6 flex flex-col gap-4 justify-stretch">
                  <h3 className="text-[24px]">Unassign {participantProfile?.FriendlyName} from {participantProfile?.ParticipantName}?</h3>
                  <Button variant='primary' onClick={() => unassignBand()}>Unassign Band</Button>
                  <Button variant='quaternary' onClick={() => setOpen(false)}>Cancel</Button>
              </div>
          </div>
      );
    };

    const checkOutUser = async () => {
      await Sentry.startSpan({ name: "Check Out User", op: "http.request" }, async (span) => {
        try {
          const url = `${import.meta.env.VITE_API_URL}Bands/CheckOut?bandId=${checkedInBand.BandID}&userTriggering=${user.id}&orgId=${selectedVenue?.OrgID}&zoneId=${checkedInBand.CurrentZone}&isManual=1`;
          span.setAttribute("http.url", url);
    
          const response = await fetchWithRefresh(url, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${user?.token}`,
            },
          }, logout, updateTokens, (message) => { toast.info(message);});
    
          if (response.ok) {
            setOpen(true);
            setDrawerContent(
              <div className="w-full">
                <div className="flex items-center justify-between w-full">
                  <br />
                  <h2 className="leading-5 tracking-widest uppercase text-[12px]">
                    CHECKED OUT
                  </h2>
                  <CloseIcon className='cursor-pointer' onClick={closeModal} />
                </div>
                <div className="mt-6 flex flex-col gap-6 justify-stretch">
                  <h3>Checked Out of {checkedInBand?.ZoneName} Successfully!</h3>
                  <Button onClick={closeModal} className="w-full">Close</Button>
                </div>
              </div>
            );
          } else {
            throw new Error("Failed to check band out of zone.");
          }
        } catch (error) {
          Sentry.captureException(error, { extra: { BandID: checkedInBand.BandID, UserID: user.id } });
          LogErrors(user, logout, updateTokens, "Couldn't check out.");
        } finally {
          closeModal();
          span.end();
        }
      });
    };
    
    const checkOutUserNoActivity = async () => {
      await Sentry.startSpan({ name: "Check Out User (No Activity)", op: "http.request" }, async (span) => {
        try {
          const url = `${import.meta.env.VITE_API_URL}Bands/CheckOutNoActivity?bandId=${checkedInBand.BandID}&userTriggering=${user.id}&orgId=${selectedVenue?.OrgID}&zoneId=${checkedInBand.CurrentZone}&isManual=1`;
          span.setAttribute("http.url", url);
    
          const response = await fetchWithRefresh(url, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${user?.token}`,
            },
          }, logout, updateTokens, (message) => { toast.info(message);});
    
          if (response.ok) {
            setOpen(true);
            setDrawerContent(
              <div className="w-full">
                <div className="flex items-center justify-between w-full">
                  <br />
                  <h2 className="leading-5 tracking-widest uppercase text-[12px]">
                    CHECKED OUT
                  </h2>
                  <CloseIcon className='cursor-pointer' onClick={closeModal} />
                </div>
                <div className="mt-6 flex flex-col gap-6 justify-stretch">
                  <h3>Checked Out of {checkedInBand?.ZoneName} Successfully!</h3>
                  <Button onClick={closeModal} className="w-full">Close</Button>
                </div>
              </div>
            );
            
  
          } else {
            throw new Error(`Failed to check band out of zone. Status: ${response.status} ${response.statusText}`);
          }
        } catch (error) {
          Sentry.captureException(error, { extra: { BandID: checkedInBand.BandID, UserID: user.id } });
          LogErrors(user, logout, updateTokens, "Couldn't reach checkout endpoint: " + error);
        } finally {
          span.end();
        }
      });
    };

    const deactivateAndCheckOut = async () => {
      await Sentry.startSpan({ name: "Unassign & Check Out", op: "http.request" }, async (span) => {
        try {
          const checkoutUrl = `${import.meta.env.VITE_API_URL}Bands/CheckOutNoActivity?bandId=${checkedInBand.BandID}&userTriggering=${user.id}&orgId=${selectedVenue?.OrgID}&zoneId=${checkedInBand.CurrentZone}&isManual=1`;
          const checkoutResponse = await fetchWithRefresh(checkoutUrl, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${user?.token}`,
            },
          }, logout, updateTokens, (message) => { toast.info(message); });

          if (!checkoutResponse.ok) {
            throw new Error(`Failed to check band out of zone. Status: ${checkoutResponse.status} ${checkoutResponse.statusText}`);
          }

          const bookoutUrl = `${import.meta.env.VITE_API_URL}scan/bookout?bookingParticipantId=${participantProfile?.BookingParticipantId}&scanId=${checkedInBand?.BandID}&bookingId=${participantProfile?.BookingId}`;
          span.setAttribute("http.url", bookoutUrl);

          const bookoutResponse = await fetchWithRefresh(bookoutUrl, {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${user?.token}`,
            },
          }, logout, updateTokens, (message) => { toast.info(message); });

          if (!bookoutResponse.ok) {
            const contentType = bookoutResponse.headers.get("content-type");
            const errorMsg = contentType && contentType.includes("application/json") ? await bookoutResponse.json() : await bookoutResponse.text();
            throw new Error(errorMsg);
          }

          setOpen(true);
          setDrawerContent(
            <div className="w-full">
              <div className="flex items-center justify-between w-full">
                <br />
                <h2 className="leading-5 tracking-widest uppercase text-[12px]">Booked Out</h2>
                <CloseIcon className='cursor-pointer' onClick={closeModal} />
              </div>
              <div className="mt-6 flex flex-col gap-6 justify-stretch">
                <h3>Band Booked Out & Checked Out Successfully!</h3>
                <Button onClick={closeModal} className="w-full">Close</Button>
              </div>
            </div>
          );
        } catch (error) {
          Sentry.captureException(error, { extra: { BandID: checkedInBand?.BandID, BookingParticipantId: participantProfile?.BookingParticipantId, BookingId: participantProfile?.BookingId } });
          LogErrors(user, logout, updateTokens, "Couldn't complete unassign & check out: " + error);
        } finally {
          span.end();
        }
      });
    };

    const fetchMilestoneRequirements = async () => {
        await Sentry.startSpan({ name: "Check Milestones", op: "http.request" }, async (span) => {
            try {
                const url = `${import.meta.env.VITE_API_URL}Milestone/CanParticipantBeAwardedMilestonesForZoneCheckOut?participantId=${checkedInBand?.participantId}&zoneId=${checkedInBand?.ZoneID}`;
                span.setAttribute("http.url", url);
                span.setAttribute("participantId", participantId);
                span.setAttribute("zoneId", checkedInBand?.CurrentZone);
          
                const response = await fetchWithRefresh(
                  url,
                  {
                    headers: {
                      "Content-Type": "application/json",
                      Authorization: "Bearer " + user.token,
                    },
                  }, logout, updateTokens, (message) => { toast.info(message);}
                );
          
                if (!response.ok) {
                  const errorText = await response.text();
                  LogErrors(user, logout, updateTokens, `Failed to check milestone. Status: ${response.status}, Response: ${errorText}`);
                  span.setStatus("error");
                  throw new Error(`Failed to check milestone: ${errorText}`);
                }
          
                const milestoneResponse = await response.json();
                setMilestoneRequirements(milestoneResponse);
                span.setStatus("ok");
            } catch (error) {
                Sentry.captureException(error, { extra: { participantId: participantId, zoneId: checkedInBand?.CurrentZone } });
                LogErrors(user, logout, updateTokens, "Error checking milestones: " + error);
                span.setStatus("error");
                throw error;
            } finally {
                span.end();
            }
        });
    }

    const closeModal = () => {
      setOpen(false);
      navigate('/')
    };

    const isInBooking = participantProfile?.inBooking === 1 || participantProfile?.inBooking === "1";

    console.log(checkedInBand)

    return (
      <div className='mt-[20px]'>
        {!participantProfile ? (
          <div className="flex justify-center items-center h-full">
            <div className="loading loading-spinner loading-lg">Loading...</div>
          </div>
        ) : (
          <>
            <div className='p-[24px] mb-[15px]'>
              {bands.some(b => b.IsGuestBand === 1) ? (
                <BandCard
                  band={bands.find(b => b.IsGuestBand === 1)}
                  participant={{ ParticipantName: participantProfile?.ParticipantName }}
                  size="sm"
                  label="GUEST BAND"
                />
              ) : user?.subscription?.Name === "Lite" ? (
                <div className="bg-[#F8F8F8] border-[1px] border-[#E3E3E3] h-[200px] rounded-[4px] mr-5 relative flex items-center p-3">
                  <div className="flex flex-col gap-2 font-medium">
                    {participantProfile?.ParticipantName}'s Profile
                  </div>
                  <img
                    src="/images/grey-band.png"
                    alt="band"
                    className="max-w-[175px] mx-auto right-[-20px] absolute"
                  />
                </div>
              ) : (
                <ParticipantCard
                  participant={participantProfile}
                  key={participantProfile?.ParticipantId}
                  location="profile"
                />
              )}
              
              <div className='mt-[30px] mb-[30px] pb-[30px] border-b border-dashed border-[#E3E3E3]'>
                {theme !== "staff" && (
                  <>
                    {user?.subscription?.Name === "Lite" ? (
                      <div className='flex flex-row justify-between border border-sportsLightGrey rounded-[4px] p-[20px] cursor-pointer' onClick={showLiteDrawer}>
                        <span className='font-semibold'>{participantProfile?.ParticipantName.split(" ")[0]}'s Bands</span>
                        <ChevronRight className='w-[24px] h-[24px] fill-black'/>
                      </div>
                    ) : (
                      <Link className='flex flex-row justify-between border border-sportsLightGrey rounded-[4px] p-[20px]' to={`/participant/${participantProfile?.ParticipantId}/bands`} state={{ participant: participantProfile }}>
                        <span className='font-semibold'>{participantProfile?.ParticipantName.split(" ")[0]}'s Bands</span>
                        <ChevronRight className='w-[24px] h-[24px] fill-black'/>
                      </Link>
                    )}
                  </>
                )}
              </div>
              
              <div className='flex flex-col gap-4'>
                <div className='flex flex-col gap-y-1'>
                  <p className='font-poppins font-semibold text-[16px]'>Date of Birth</p>
                  <p className='font-poppins text-[#727272]'>
                  {participantProfile?.DateOfBirth 
                      ? isNaN(new Date(participantProfile.DateOfBirth).getTime()) 
                          ? "(invalid date)" 
                          : format(new Date(participantProfile.DateOfBirth), 'dd/MM/yyyy') 
                      : "(not provided)"
                  }
                  </p>
                </div>
                <div className='flex flex-col gap-y-1'>
                  <p className='font-poppins font-semibold text-[16px]'>Primary Emergency Contact Name (ICE)</p>
                  <p className='font-poppins text-[#727272]'>{participantProfile?.PrimaryIceName?.trim() ? participantProfile.PrimaryIceName : "(not provided)"}</p>
                </div>
                <div className='flex flex-col gap-y-1 justify-between'>
                  <div>
                    <p className='font-poppins font-semibold text-[16px]'>Primary Emergency Contact Number</p>
                    <Link to={`tel:${participantProfile?.PrimaryIceNumber}`} className='font-poppins text-[#727272] underline'>{participantProfile?.PrimaryIceNumber}</Link>
                  </div>
                </div>
                {user?.subscription?.Name === "Lite" ? (
                  <div onClick={showLiteDrawer} className="cursor-pointer">
                    <Toggle
                      name="AutomaticSMS"
                      label="Automatic SMS"
                      defaultChecked={participantProfile?.EmergencySMSPrimaryContact === 1 ? true : false}
                      disabled
                      labelClassName="!font-semibold"
                    />
                  </div>
                ) : (
                  <Toggle
                    name="AutomaticSMS"
                    label="Automatic SMS"
                    defaultChecked={participantProfile?.EmergencySMSPrimaryContact === 1 ? true : false}
                    disabled
                    labelClassName="!font-semibold"
                  />
                )}
                <div className='flex flex-col gap-y-1'>
                  <p className='font-poppins font-semibold text-[16px]'>Primary Emergency Contact Relationship</p>
                  <p className='font-poppins text-[#727272]'>{participantProfile?.PrimaryIceRelationship?.trim() ? participantProfile.PrimaryIceRelationship : "(not provided)"}</p>
                </div>
                <div className='flex flex-col gap-y-1'>
                  <p className='font-poppins font-semibold text-[16px]'>Primary Medical Condition</p>
                  <p className='font-poppins text-[#727272]'>{participantProfile?.PrimaryMedicalCondition}</p>
                </div>
    
{user?.subscription?.Name === "Lite" ? (
                  <div className='flex flex-col gap-y-1 relative cursor-pointer' onClick={showLiteDrawer}>
                    <p className='font-poppins font-semibold text-[16px]'>Voice Note</p>
                    <div className="pointer-events-none">
                      <VoiceNote existingAudio={participantProfile?.AudioUrl} label={false} editable={false}/>
                    </div>
                    <div className="absolute inset-0" />
                  </div>
                ) : (
                  participantProfile?.AudioUrl && (
                    <div className='flex flex-col gap-y-1'>
                      <p className='font-poppins font-semibold text-[16px]'>Voice Note</p>
                      <VoiceNote existingAudio={participantProfile?.AudioUrl} label={false} editable={false}/>
                    </div>
                  )
                )}
    
                <div className='flex flex-col gap-y-1'>
                  <p className='font-poppins font-semibold text-[16px]'>Notes</p>
                  <p className='font-poppins text-[#727272]'>{participantProfile?.Notes ? participantProfile?.Notes : 'You can add notes here. For example, your blood group or NHS number.'}</p>
                </div>
              </div>
            </div>
            <div className='flex flex-col gap-y-[10px] bg-sportsLightGrey p-[24px]'>
              {medicalConditions.length > 0 && (
                <DropDown title='Medical History' icon="medical">
                  <div className='flex flex-col gap-y-3'>
                  {medicalConditions?.map((condition, index) => (
                      <div key={index} className='flex flex-col'>
                        {condition.ConditionName && (<p className='font-poppins text-[16px] font-semibold'>{condition.ConditionName}</p>)}
                        {condition.DateAdded && (
                          <p className='font-poppins text-[#727272]'>Added: {format(new Date(condition.DateAdded), 'dd/MM/yyyy')}</p> 
                        )}
                        {condition.OnGoing === 1 && (<p className='font-poppins text-[#727272]'>Ongoing</p>)}
                        {condition.Notes && (<p className='font-poppins text-[#727272]'>Additional Information: {condition.Notes}</p>)}
                      </div>
                    ))}
                  </div>
                </DropDown>
              )}
              {allergies.length > 0 && (
                <DropDown title="Allergies" icon="allergy">
                  <div className='flex flex-col gap-y-3'>
                  {allergies?.map((allergy, index) => (
                    <div key={index} className='flex flex-col'>
                      <p className='font-poppins font-semibold text-[16px]'>{allergy?.ConditionName}</p>
                      <p className='font-poppins text-[#727272]'>Added: {format(new Date(allergy.DateAdded), 'dd/MM/yyyy')}</p>
                      {allergy.Notes && (<p className='font-poppins text-[#727272]'>Additional Information: {allergy.Notes}</p>)}
                    </div>
                  ))}
                  </div>
                </DropDown>
              )}
              {medications.length > 0 && (
                <DropDown title="Medications" icon="medication">
                  <div className='flex flex-col gap-y-3'>
                    {medications?.map((medication, index) => (
                      <div key={index} className='flex flex-col'>
                        <p className='font-poppins text-[16px] font-semibold'>{medication.MedicationName}</p>
                        {medication.Dosage && <p className='font-poppins text-[#727272]'>Dosage: {medication.Dosage}</p>}
                        {medication.Frequency && <p className='font-poppins text-[#727272]'>Frequency: {medication.Frequency}</p>}
                        {medication.OnGoing === 1 && (<p className='font-poppins text-[#727272]'>Ongoing</p>)}
                        {medication.Notes && <p className='font-poppins text-[#727272]'>Additional Information: {medication.Notes}</p>}
                      </div>
                    ))}
                  </div>
                </DropDown>
              )}
              {otherEmergencyContacts.length > 0 && (
                <DropDown title="Other Emergency Contacts" icon="contact">
                  <div className='flex flex-col gap-y-3'>
                    {otherEmergencyContacts?.map((contact, index) => (
                      <div key={index} className='flex flex-col'>
                        <p className='font-poppins font-semibold text-[16px]'>{contact.AdditionalICEName}</p>
                        <p className='font-poppins text-[#727272]'>{contact.AdditionalICERelationship}</p>
                        <Link to={`tel:${contact.AdditionalICENumber}`} className='font-poppins text-[#727272] underline'>{contact.AdditionalICENumber}</Link>
                        <Toggle 
                          name={`AdditionalAutomaticSMS${index}`}
                          label="Automatic SMS"
                          defaultChecked={contact.EmergencySMS ? true : false }
                          disabled
                        />
                      </div>
                    ))}
                  </div>
                </DropDown>
              )}
    
              {incidentHistory.length > 0 && (
                <Link to={`/participant/${participantProfile?.ParticipantId}/incident-history`}>
                  <div
                    className={`flex flex-col justify-between bg-white border rounded-[8px] p-6`}
                    data-testid="dropdown-container"
                  >
                    <div className={`flex justify-between min-w-full`}>
                      <div className="flex flex-row items-center gap-x-[20px]">
                        <IncidentIcon className="w-[24px] h-[24px]" data-testid="icon" />
                        <div className="flex cursor-pointer flex-row w-full justify-between items-center font-semibold antialiased">
                          Incident History
                        </div>
                      </div>
                      <div className="">
                        <ChevronRight
                          data-testid="dropdown-icon"
                          className={`fill-[#000000] transition-all cursor-pointer`}
                        />
                      </div>
                    </div>
                  </div>
                </Link>
              )}
            </div>

            <div className='flex flex-col pt-[24px] pb-[95px] gap-y-[10px] p-y-[31px] px-[24px] bg-sportsLightGrey'>
              {theme === "staff" ? (
                <>
                  {bands.some(b => b.IsGuestBand === 1) && (
                    <Button variant='primary' onClick={confirmUnassignBand}>Unassign Band</Button>
                  )}
                  {checkedInBand?.BandStateVerbose === "CHECKED IN" && milestoneRequirements?.MilestonesAvailable === 1 && (
                    <Button variant='primary' onClick={checkOutUser}>Award Milestone & Check Out</Button>
                  )}
                  {checkedInBand?.BandStateVerbose === "CHECKED IN" && (
                    <Button variant='primary' onClick={checkOutUserNoActivity}>Check Out - No Milestone</Button>
                  )}
                  {checkedInBand?.BandStateVerbose === "CHECKED IN" && isInBooking && participantProfile?.BookingId && participantProfile?.BookingParticipantId && (
                    <Button variant='primary' className='!bg-[#A6093D] text-white' onClick={deactivateAndCheckOut}>Book Out &amp; Check Out</Button>
                  )}

                </>
              ) : (
                !transferStatus ? (
                  <button className='underline bg-transparent text-black font-semibold' onClick={user?.subscription?.Name === "Lite" ? showLiteDrawer : transferParticipant}>Transfer Participant</button>
                ) : (
                  <button className='underline bg-transparent text-black font-semibold' onClick={cancelTransfer}>Cancel Transfer</button>
                )
              )}
            </div>
          </>
        )}
      </div>
    );
}